<template>
    <MainContent :title="abonado ? `${abonado.name} ${abonado.surnames}` : 'Abonado'">
        <template #header></template>

        <div v-if="manager.lfp" class="tw:bg-blue-50 tw:border tw:border-blue-200 tw:rounded-lg tw:p-4 tw:mb-6">
            <p class="tw:text-blue-800 tw:text-sm tw:flex tw:items-center tw:gap-2 tw:m-0">
                <i class="fas fa-info-circle"></i>
                Para más información, por favor consulte AVET
            </p>
        </div>

        <div v-if="teatro.sga" class="tw:bg-blue-50 tw:border tw:border-blue-200 tw:rounded-lg tw:p-4 tw:mb-6">
            <p class="tw:text-blue-800 tw:text-sm tw:flex tw:items-center tw:gap-2 tw:m-0">
                <i class="fas fa-info-circle"></i>
                Para más información, por favor consulte SGA
            </p>
        </div>

        <div v-if="loading" class="tw:grid tw:grid-cols-1 tw:gap-6">
            <!-- Skeleton: Left + Right cards side by side -->
            <div class="tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-6">
                <!-- Skeleton: DatosAbonadoCard -->
                <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-100 tw:overflow-hidden">
                    <div class="tw:bg-gray-800 tw:opacity-30 tw:px-6 tw:py-4">
                        <SkeletonItem height="tw:h-4" customClass="tw:w-40 tw:bg-white/40" />
                    </div>
                    <div class="tw:p-6 tw:space-y-5">
                        <!-- Section: Nombre, Nº, Antiguo, Género en box -->
                        <div class="tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-lg tw:p-4">
                            <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                                <div v-for="n in 4" :key="'pers-' + n" class="tw:space-y-1.5">
                                    <SkeletonItem height="tw:h-2.5" customClass="tw:w-24" />
                                    <SkeletonItem height="tw:h-4" customClass="tw:w-4/5" />
                                </div>
                            </div>
                        </div>
                        <!-- Divider + Datos de contacto -->
                        <div class="tw:border-t tw:border-gray-100 tw:pt-1"></div>
                        <div class="tw:flex tw:items-center tw:gap-1.5">
                            <SkeletonItem height="tw:h-3" customClass="tw:w-3 tw:rounded-full" />
                            <SkeletonItem height="tw:h-3" customClass="tw:w-32" />
                        </div>
                        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                            <div v-for="n in 3" :key="'contact-' + n" class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-2.5" customClass="tw:w-20" />
                                <SkeletonItem height="tw:h-4" customClass="tw:w-3/4" />
                            </div>
                        </div>
                        <!-- Divider + Datos personales adicionales -->
                        <div class="tw:border-t tw:border-gray-100 tw:pt-1"></div>
                        <div class="tw:flex tw:items-center tw:gap-1.5">
                            <SkeletonItem height="tw:h-3" customClass="tw:w-3 tw:rounded-full" />
                            <SkeletonItem height="tw:h-3" customClass="tw:w-36" />
                        </div>
                        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                            <div v-for="n in 2" :key="'extra-' + n" class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-2.5" customClass="tw:w-24" />
                                <SkeletonItem height="tw:h-4" customClass="tw:w-3/4" />
                            </div>
                        </div>
                        <!-- Divider + Domicilio -->
                        <div class="tw:border-t tw:border-gray-100 tw:pt-1"></div>
                        <div class="tw:flex tw:items-center tw:gap-1.5">
                            <SkeletonItem height="tw:h-3" customClass="tw:w-3 tw:rounded-full" />
                            <SkeletonItem height="tw:h-3" customClass="tw:w-20" />
                        </div>
                        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                            <div v-for="n in 5" :key="'addr-' + n" class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-2.5" customClass="tw:w-20" />
                                <SkeletonItem height="tw:h-4" customClass="tw:w-4/5" />
                            </div>
                        </div>
                        <!-- Divider + Datos laborales -->
                        <div class="tw:border-t tw:border-gray-100 tw:pt-1"></div>
                        <div class="tw:flex tw:items-center tw:gap-1.5">
                            <SkeletonItem height="tw:h-3" customClass="tw:w-3 tw:rounded-full" />
                            <SkeletonItem height="tw:h-3" customClass="tw:w-28" />
                        </div>
                        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                            <div v-for="n in 2" :key="'job-' + n" class="tw:space-y-1.5">
                                <SkeletonItem height="tw:h-2.5" customClass="tw:w-20" />
                                <SkeletonItem height="tw:h-4" customClass="tw:w-3/4" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Skeleton: DatosAbonoCard -->
                <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-100 tw:overflow-hidden">
                    <!-- Header azul con botón -->
                    <div class="tw:bg-gray-800 tw:opacity-30 tw:px-6 tw:py-4 tw:flex tw:items-center tw:justify-between">
                        <SkeletonItem height="tw:h-4" customClass="tw:w-36 tw:bg-white/40" />
                        <SkeletonItem height="tw:h-6" customClass="tw:w-24 tw:rounded tw:bg-white/30" />
                    </div>
                    <div class="tw:p-6 tw:space-y-5">
                        <!-- Butaca: box azul claro con label + texto + botón CAMBIAR -->
                        <div class="tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:space-y-2">
                            <SkeletonItem height="tw:h-2.5" customClass="tw:w-14" />
                            <div class="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:flex-wrap">
                                <SkeletonItem height="tw:h-4" customClass="tw:w-3/4" />
                                <SkeletonItem height="tw:h-7" customClass="tw:w-24 tw:rounded-lg" />
                            </div>
                        </div>
                        <!-- Tipo de abono + Precio: box gris 2 cols + botón cambiar -->
                        <div class="tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-lg tw:p-4">
                            <div class="tw:grid tw:grid-cols-2 tw:gap-4">
                                <div class="tw:space-y-1.5">
                                    <SkeletonItem height="tw:h-2.5" customClass="tw:w-24" />
                                    <SkeletonItem height="tw:h-4" customClass="tw:w-4/5" />
                                </div>
                                <div class="tw:space-y-1.5">
                                    <SkeletonItem height="tw:h-2.5" customClass="tw:w-14" />
                                    <SkeletonItem height="tw:h-4" customClass="tw:w-3/4" />
                                </div>
                            </div>
                            <!-- Botón cambiar tipo -->
                            <div class="tw:col-span-2 tw:pt-3 tw:mt-3 tw:border-t tw:border-gray-200">
                                <SkeletonItem height="tw:h-7" customClass="tw:w-48 tw:rounded-lg" />
                            </div>
                        </div>
                        <!-- Fechas: 2 boxes ambar lado a lado, cada uno con icono + label + valor -->
                        <div class="tw:grid tw:grid-cols-2 tw:gap-3">
                            <div v-for="n in 2" :key="'fecha-' + n" class="tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-lg tw:p-3 tw:flex tw:items-start tw:gap-3">
                                <SkeletonItem height="tw:h-4" customClass="tw:w-4 tw:rounded-full tw:shrink-0 tw:mt-0.5" />
                                <div class="tw:flex-1 tw:space-y-1.5">
                                    <SkeletonItem height="tw:h-2.5" customClass="tw:w-24" />
                                    <SkeletonItem height="tw:h-4" customClass="tw:w-full" />
                                </div>
                            </div>
                        </div>
                        <!-- Divider + Información adicional: 7 campos con col-span donde corresponde -->
                        <div class="tw:border-t tw:border-gray-100 tw:pt-1"></div>
                        <div class="tw:space-y-3">
                            <div class="tw:flex tw:items-center tw:gap-1.5">
                                <SkeletonItem height="tw:h-3" customClass="tw:w-3 tw:rounded-full" />
                                <SkeletonItem height="tw:h-3" customClass="tw:w-36" />
                            </div>
                            <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                                <!-- Observaciones: col-span-2 -->
                                <div class="tw:col-span-2 tw:space-y-1.5">
                                    <SkeletonItem height="tw:h-2.5" customClass="tw:w-24" />
                                    <SkeletonItem height="tw:h-4" customClass="tw:w-1/2" />
                                </div>
                                <!-- IBAN, Titular -->
                                <div v-for="n in 2" :key="'iban-' + n" class="tw:space-y-1.5">
                                    <SkeletonItem height="tw:h-2.5" customClass="tw:w-20" />
                                    <SkeletonItem height="tw:h-4" customClass="tw:w-4/5" />
                                </div>
                                <!-- Vendido por: col-span-2 -->
                                <div class="tw:col-span-2 tw:space-y-1.5">
                                    <SkeletonItem height="tw:h-2.5" customClass="tw:w-20" />
                                    <SkeletonItem height="tw:h-4" customClass="tw:w-3/4" />
                                </div>
                                <!-- Origen, Código, QR -->
                                <div v-for="n in 3" :key="'extra-' + n" class="tw:space-y-1.5">
                                    <SkeletonItem height="tw:h-2.5" customClass="tw:w-16" />
                                    <SkeletonItem height="tw:h-4" customClass="tw:w-4/5" />
                                </div>
                            </div>
                        </div>
                        <!-- Divider + Impresión / Envío: pills SI/NO -->
                        <div class="tw:border-t tw:border-gray-100 tw:pt-1"></div>
                        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                            <div v-for="n in 2" :key="'print-' + n" class="tw:space-y-2">
                                <SkeletonItem height="tw:h-2.5" customClass="tw:w-20" />
                                <div class="tw:flex tw:items-center tw:gap-3">
                                    <SkeletonItem height="tw:h-5" customClass="tw:w-12 tw:rounded-full" />
                                    <SkeletonItem height="tw:h-3" customClass="tw:w-28 tw:bg-gray-100" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Skeleton: TabsPanel -->
            <div id="abonado-tabs" class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-200 tw:overflow-hidden">
                <!-- Tab nav skeleton: 11 pestañas, la primera activa -->
                <div class="tw:border-b tw:border-gray-200">
                    <nav class="tw:flex tw:gap-3 tw:-mb-px tw:overflow-x-auto tw:px-4">
                        <!-- Primera pestaña activa (borde azul) -->
                        <div class="tw:px-4 tw:py-3 tw:border-b-2 tw:border-blue-600">
                            <SkeletonItem height="tw:h-3" customClass="tw:w-24 tw:bg-blue-200" />
                        </div>
                        <!-- Resto inactivas -->
                        <div v-for="n in 10" :key="'stab-' + n" class="tw:px-4 tw:py-3 tw:border-b-2 tw:border-transparent">
                            <SkeletonItem height="tw:h-3" customClass="tw:w-20 tw:bg-gray-200" />
                        </div>
                    </nav>
                </div>
                <!-- Tab content: Transacciones table skeleton -->
                <div class="tw:relative tw:bg-transparent">
                    <!-- Header -->
                    <div class="tw:flex tw:justify-between tw:items-end tw:px-6 tw:py-4 tw:border-b tw:border-gray-100">
                        <div class="tw:flex tw:flex-col tw:gap-1">
                            <SkeletonItem height="tw:h-4" customClass="tw:w-32" />
                            <SkeletonItem height="tw:h-3" customClass="tw:w-48 tw:bg-gray-100" />
                        </div>
                        <SkeletonItem height="tw:h-4" customClass="tw:w-24" />
                    </div>
                    <!-- Table -->
                    <div class="tw:w-full tw:overflow-x-auto">
                        <table class="tw:w-full tw:text-sm tw:border-collapse">
                            <!-- Table header -->
                            <thead>
                                <tr class="tw:border-b tw:border-gray-100">
                                    <th v-for="n in 8" :key="'th-' + n" class="tw:px-6 tw:py-3 tw:text-left">
                                        <SkeletonItem height="tw:h-3" customClass="tw:w-24" />
                                    </th>
                                </tr>
                            </thead>
                            <!-- Skeleton rows -->
                            <tbody class="tw:divide-y tw:divide-gray-100 tw:animate-pulse">
                                <tr v-for="row in 3" :key="'srow-' + row" class="tw:border-b tw:border-gray-100">
                                    <td v-for="col in 8" :key="'scol-' + col" class="tw:px-6 tw:py-4">
                                        <SkeletonItem height="tw:h-4" customClass="tw:w-4/5 tw:bg-gray-100" />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Skeleton: DatosLegalesCard -->
            <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-200 tw:p-6">
                <!-- Título -->
                <SkeletonItem height="tw:h-4" customClass="tw:w-32 tw:mb-2" />
                <!-- Toggles -->
                <div class="tw:space-y-4 tw:mt-4">
                    <!-- Política de privacidad: ON + (No editable) -->
                    <div class="tw:flex tw:items-center tw:gap-2">
                        <SkeletonItem height="tw:h-5" customClass="tw:w-9 tw:rounded-full tw:bg-gray-200" />
                        <div class="tw:space-y-1">
                            <SkeletonItem height="tw:h-3" customClass="tw:w-48" />
                        </div>
                    </div>
                    <!-- Condiciones de uso: ON + (No editable) -->
                    <div class="tw:flex tw:items-center tw:gap-2">
                        <SkeletonItem height="tw:h-5" customClass="tw:w-9 tw:rounded-full tw:bg-gray-200" />
                        <div class="tw:space-y-1">
                            <SkeletonItem height="tw:h-3" customClass="tw:w-40" />
                        </div>
                    </div>
                    <!-- Comunicaciones comerciales: ON, sin subtítulo -->
                    <div class="tw:flex tw:items-center tw:gap-2">
                        <SkeletonItem height="tw:h-5" customClass="tw:w-9 tw:rounded-full tw:bg-gray-200" />
                        <SkeletonItem height="tw:h-3" customClass="tw:w-52" />
                    </div>
                </div>
                <!-- Botón guardar -->
                <div class="tw:flex tw:justify-end tw:mt-6 tw:pt-4 tw:border-t tw:border-gray-200">
                    <SkeletonItem height="tw:h-8" customClass="tw:w-44 tw:rounded-lg" />
                </div>
            </div>
        </div>

        <div v-else-if="abonado" class="tw:grid tw:grid-cols-1 tw:gap-6">
            <div class="tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-6">
                <DatosAbonadoCard
                    :abonado="abonado"
                    :manager="manager"
                />
                <DatosAbonoCard
                    :abonado="abonado"
                    :manager="manager"
                    :header-options="getHeaderOptions()"
                    @actualizar="refreshAbonado"
                    @cambiar-asiento="onCambiarAsiento"
                    @cambiar-precio="onCambiarPrecio"
                />
            </div>

            <TabsPanel id="abonado-tabs" :tabs="tabs" :initial-tab="$route.query.tab" @tab-change="onTabChange">
                <template #tab-plazos>
                    <PlazosCard
                        :plazos="abonado.plazos"
                        :data="abonado"
                        @pagar-plazo="(plazo_id, metodo_pago) => pagarPlazo(plazo_id, metodo_pago)"
                        @pagar-plazos="(metodo_pago) => pagarPlazos(metodo_pago)"
                        @descontar-plazo="(descuento_integro, descuento_porcentaje, plazo_id) => descontarPlazo(descuento_integro, descuento_porcentaje, plazo_id)"
                        @generar-enlace-pago="generarEnlacePago"
                    />
                </template>
                <template #tab-transacciones>
                    <TransaccionesCard
                        :transacciones="abonado.transacciones"
                        :abonado="abonado"
                        @editar-transaccion="onEditarTransaccion"
                    />
                </template>
                <template #tab-eventos>
                    <EventosAsistidosCard
                        :eventos="abonado.eventos_asistidos"
                        :temporada="abonado.temporada"
                    />
                </template>
                <template #tab-sanciones>
                    <SancionesCard
                        :sanciones="abonado.sanciones || []"
                        :abonado="abonado"
                        @add-sancion="onAddSancion"
                        @borrar-sancion="onBorrarSancion"
                    />
                </template>
                <template #tab-monedero>
                    <MonederoCard
                        :abonado="abonado"
                        @add-monedero="onAddMonedero"
                    />
                </template>
                <template #tab-invitaciones>
                    <InvitacionesCard
                        :abonado="abonado"
                        :lista_id="props.lista_id"
                        :items="state.invitaciones.data"
                        :loading="state.invitaciones.loading"
                        :page="state.invitaciones.page"
                        :pageSize="state.invitaciones.pageSize"
                        :total="state.invitaciones.total"
                        :invitaciones-consumidas="invitacionesConsumidas"
                        @paginate="(payload) => fetchInvitaciones(payload.page)"
                        @open-modal="openModal"
                        @close-modal="closeModal"
                    />
                </template>
                <template #tab-renovacion>
                    <RenovacionAutomaticaCard
                        :abonado="abonado"
                        @update-abonado="editarRenovacionAutomatica"
                    />
                </template>
                <template #tab-impresos>
                    <ImpresosCard
                        :items="state.impresos.data"
                        :loading="state.impresos.loading"
                        :page="state.impresos.page"
                        :pageSize="state.impresos.pageSize"
                        :total="state.impresos.total"
                        @paginate="(payload) => fetchImpresos(payload.page)"
                    />
                </template>
                <template #tab-asientos_liberados>
                    <AsientosLiberadosCard
                        :items="state.asientos.data"
                        :loading="state.asientos.loading"
                        :show-recuperar="true"
                        @liberar="liberarAsiento"
                    />
                </template>
                <template #tab-observaciones>
                    <ObservacionesCard
                        :observaciones="abonado?.observaciones || ''"
                        @guardar="editarObservacion"
                    />
                </template>
                <template #tab-cesion_historial>
                    <AdminCesionHistorial :abonado-id="abonado.id" />
                </template>
                <template #tab-documentos>
                    <DocumentosCard
                        :documentos="state.documentos.data"
                        :url="url_documentacion"
                        :abonado-id="abonado_id"
                        :selected-doc="state.modals.selectedDocumento"
                        @edit="openEditDocumentoModal"
                        @delete="deleteDocumento"
                        @upload-success="refreshAbonado"
                        @close-delete-modal="closeDeleteDocumentoModal"
                        @confirm-delete="confirmDeleteDocumento"
                        @close-edit-modal="closeEditDocumentoModal"
                        @confirm-edit="confirmEditDocumento"
                    />
                </template>
                <template #tab-familia>
                    <MiembrosTab
                        :family="familiaFamily"
                        :members="familiaFamily?.miembros || []"
                        :loading="familiasLoading"
                        :error="familiasError"
                        :has-family="!!familiaFamily"
                        :abonado="abonado"
                        :create-loading="familiaCreateLoading"
                        :api-create-error="familiaCreateError"
                        @add-member="handleAddMember"
                        @remove-member="handleRemoveMember"
                        @create-family="handleCreateFamily"
                        @update-nombre="handleUpdateNombre"
                        @retry="handleRetry"
                    />
                </template>
            </TabsPanel>
            <DatosLegalesCard
                :abonado="abonado"
                @editar-datos-legales="editarDatosLegales"
            />
            <!-- Modals: mount modal components so $modal.show can find them -->
            <AddSancion :abonado="abonado" @close="onSancionClose" />
            <BorrarSancion v-if="modalBorrarSancion" :abonado="abonado" :sancion="getSancion(modalBorrarSancion)" @close="onBorrarSancionClose" />
            <AddMonedero :abonado="abonado" @close="onMonederoClose" />
            <CambiarAsiento v-if="abonado" :abonado="abonado" @close="onCambiarAsientoClose" />
            <CambiarPrecio v-if="abonado" :abonado="abonado" @close="onCambiarPrecioClose" />
            <EditarTransaccionModal v-if="selectedTransaccion" :transaccion="selectedTransaccion" :params="{ filters: {}, lista_id: lista_id, abonado_id: abonado_id }" @cerrar="onEditarTransaccionClose" />
        </div>
    </MainContent>
</template>

<script setup>
    import { onMounted, ref, nextTick, getCurrentInstance, computed, watch } from 'vue'
    import MainContent from '@/modules/admin/ui/MainContent.vue'
    import TabsPanel from '@/modules/admin/ui/TabsPanel.vue'
    import { SkeletonItem } from '@/modules/admin/ui/skeleton'

    import DatosAbonadoCard from '@/modules/admin/abonados/components/DatosAbonadoCard.vue'
    import DatosAbonoCard from '@/modules/admin/abonados/components/DatosAbonoCard.vue'
    import EventosAsistidosCard from '@/modules/admin/abonados/components/EventosAsistidosCard.vue'
    import ObservacionesCard from '@/modules/admin/abonados/components/ObservacionesCard.vue'
    import PlazosCard from '@/modules/admin/abonados/components/PlazosCard.vue'
    import TransaccionesCard from '@/modules/admin/abonados/components/TransaccionesCard.vue'
    import RenovacionAutomaticaCard from '@/modules/admin/abonados/components/RenovacionAutomaticaCard.vue'
    import DatosLegalesCard from '@/modules/admin/abonados/components/DatosLegalesCard.vue'
    import SancionesCard from '@/modules/admin/abonados/components/SancionesCard.vue'
    import MonederoCard from '@/modules/admin/abonados/components/MonederoCard.vue'
    import ImpresosCard from '@/modules/admin/abonados/components/ImpresosCard.vue'
    import AsientosLiberadosCard from '@/modules/admin/abonados/components/AsientosLiberadosCard.vue'
    import InvitacionesCard from '@/modules/admin/abonados/components/InvitacionesCard.vue'
    import DocumentosCard from '@/modules/admin/abonados/components/DocumentosCard.vue'
    import AdminCesionHistorial from '@/modules/admin/abonados/components/AdminCesionHistorial.vue'
    import MiembrosTab from '@/modules/admin/abonados/familias/components/MiembrosTab.vue'

    // Modales refactorizados
    import AddSancion from '@/modules/admin/abonados/modals/AddSancion.vue'
    import BorrarSancion from '@/modules/admin/abonados/modals/BorrarSancion.vue'
    import AddMonedero from '@/modules/admin/abonados/modals/addMonedero.vue'
    import CambiarAsiento from '@/modules/admin/abonados/modals/CambiarAsiento.vue'
    import CambiarPrecio from '@/modules/admin/abonados/modals/CambiarPrecio.vue'
    import EditarTransaccionModal from '@/modules/admin/transacciones/modals/EditarTransaccionModal.vue'

    import { useAbonadoShow } from '@/composables/admin/abonados/useAbonadoShow'
    import { useAbonadoCards } from '@/composables/admin/abonados/useAbonadoCards'
    import { useToast } from '@/composables/common/useToast'
    import { useFamilias, extractError } from '@/composables/admin/abonados/useFamilias'

    const props = defineProps({
        lista_id: {
            type: [String, Number],
            required: true
        },
        abonado_id: {
            type: [String, Number],
            required: true
        }
    })

    // ==================== SHOW COMPOSABLE (show-only state) ====================
    const {
        abonado,
        loading,
        manager,
        teatro,
        getAbonado,
        pagarPlazo,
        pagarPlazos,
        descontarPlazo,
        generarEnlacePago,
        editarRenovacionAutomatica,
        editarObservacion,
        editarDatosLegales,
        enviarAbono,
        url_documentacion,
    } = useAbonadoShow(props)

    // ==================== REFRESH COORDINATOR ====================
    /**
     * Orchestrates a full refresh of both show data and card data.
     * Called onMounted and after mutations (liberarAsiento, deleteDocumento, etc.)
     */
    async function refreshAbonado() {
        await getAbonado()
        
        if (instance.proxy.$route.query.tab) {
            await nextTick()
            instance.proxy.$scrollTo('#abonado-tabs', 500, { offset: -50 })
        }
        
        try {
            await Promise.allSettled([
                cards.fetchDocumentos?.(),
                cards.fetchAsientos?.()
            ])
        } catch (err) {
            // Swallow errors to keep behavior resilient
        }
    }

    // ==================== TAB CHANGE HANDLER (LAZY-FETCH) ====================
    /**
     * Handles tab activation and lazy-loads tabs on first click.
     * Impresos and Invitaciones are fetched only once, on first tab visit.
     * Pagination continues to fetch fresh data as expected.
     */
    async function onTabChange(tabKey) {
        // Lazy-fetch impresos on first click only
        if (tabKey === 'impresos' && !state.fetchedTabs.impresos) {
            await fetchImpresos()
        }
        // Lazy-fetch invitaciones on first click only
        if (tabKey === 'invitaciones' && !state.fetchedTabs.invitaciones) {
            await fetchInvitaciones()
        }
        // Lazy-fetch familia on first click only
        if (tabKey === 'familia' && !familiaFetched.value && familiaData.value?.id) {
            familiasLoading.value = true
            familiasError.value = null
            try {
                await fetchFamilia(familiaData.value.id)
                familiaFetched.value = true
            } catch (err) {
                familiasError.value = extractError(err, 'Error al cargar la familia')
            } finally {
                familiasLoading.value = false
            }
        }
    }

    // ==================== CARDS COMPOSABLE (card-specific state) ====================
    const propsRef = ref(props)
    const cards = useAbonadoCards(abonado, propsRef, refreshAbonado)

    const {
        state,
        fetchImpresos,
        fetchInvitaciones,
        fetchDocumentos,
        fetchAsientos,
        liberarAsiento,
        deleteDocumento,
        updateDocumento,
        openModal,
        closeModal
    } = cards

    const { success, error } = useToast()

    // ==================== FAMILIA COMPOSABLE ====================
    const {
        familia: familiaData,
        members,
        fetchFamilia,
        createFamily,
        addMember,
        removeMember,
        updateNombreByAbonado,
    } = useFamilias()

    const familiaFamily = computed(() => familiaData.value)
    const familiasLoading = ref(false)
    const familiasError = ref(null)
    const familiaCreateLoading = ref(false)
    const familiaCreateError = ref(null)
    const familiaFetched = ref(false)

    watch(
        () => abonado.value,
        newVal => {
            if (newVal?.familia) {
                familiaData.value = newVal.familia
            } else {
                familiaData.value = null
            }
        },
        { immediate: true }
    )

    async function handleAddMember(payload) {
        if (!familiaData.value?.id) return
        try {
            await addMember(familiaData.value.id, payload.numero_abonado, payload.dni)
        } catch (err) {
            // Error handled in composable
        }
    }

    async function handleRemoveMember(miembroId) {
        if (!familiaData.value?.id) return
        await removeMember(familiaData.value.id, miembroId)
    }

    async function handleCreateFamily(payload) {
        familiaCreateError.value = null
        familiaCreateLoading.value = true
        try {
            const result = await createFamily(props.abonado_id, payload.nombre)
            if (result) {
                familiaData.value = result?.data || result
                familiaFetched.value = false
            }
        } catch (err) {
            familiaCreateError.value = extractError(err, 'Error al crear la familia')
        } finally {
            familiaCreateLoading.value = false
        }
    }

    async function handleRetry() {
        if (!familiaData.value?.id) return
        familiasLoading.value = true
        familiasError.value = null
        try {
            await fetchFamilia(familiaData.value.id)
            familiaFetched.value = true
        } catch (err) {
            familiasError.value = extractError(err, 'Error al cargar la familia')
        } finally {
            familiasLoading.value = false
        }
    }

    async function handleUpdateNombre({ nombre, onSuccess, onError }) {
        if (!familiaData.value?.id || !nombre?.trim()) return
        try {
            await updateNombreByAbonado(props.abonado_id, nombre.trim())
            familiaData.value.nombre = nombre.trim()
            await refreshAbonado()
            onSuccess?.()
        } catch (err) {
            onError?.(extractError(err, 'Error al actualizar el nombre'))
        }
    }

    const instance = getCurrentInstance()
    const modalBorrarSancion = ref(null)
    const modalCambiarAsiento = ref(false)
    const modalCambiarPrecio = ref(false)
    const selectedTransaccion = ref(null)
    const imagenPreview = ref('')
    const invitacionesConsumidas = ref([])

    const tabs = computed(() => [
        { key: 'plazos', label: 'Plazos', show: !!(abonado.value?.plazos && abonado.value.plazos.length > 0) },
        { key: 'transacciones', label: 'Transacciones', show: !!abonado.value?.transacciones },
        { key: 'eventos', label: 'Eventos', show: true },
        { key: 'sanciones', label: 'Sanciones', show: true },
        { key: 'monedero', label: 'Monedero', show: true },
        { key: 'invitaciones', label: 'Invitaciones', show: !!abonado.value?.invitaciones_abonado },
        { key: 'renovacion', label: 'Renovación', show: true},
        { key: 'impresos', label: 'Impresos', show: true },
        { key: 'asientos_liberados', label: 'Asientos liberados', show: !!(abonado.value?.asientos_liberados && abonado.value.asientos_liberados.length > 0) },
        { key: 'observaciones', label: 'Observaciones', show: true },
        { key: 'cesion_historial', label: 'Historial cesión', show: true },
        { key: 'documentos', label: 'Documentos', show: true },
        { key: 'familia', label: 'Familia', show: !!teatro.value?.familia_abonados_activa },
    ])

    onMounted(async() => {
        await refreshAbonado();
        const initialTab = instance.proxy.$route.query.tab
        if (initialTab && ['impresos', 'invitaciones', 'familia'].includes(initialTab)) {
            onTabChange(initialTab)
        }
    })

    function getSancion(sancionId) {
        if (!abonado.value || !abonado.value.sanciones) return null
        return abonado.value.sanciones.find(s => s.id === sancionId)
    }

    function onAddSancion() {
        instance.proxy.$modal.show('addSancion')
    }

    async function onBorrarSancion({ sancionId }) {
        modalBorrarSancion.value = sancionId
        await nextTick()
        instance.proxy.$modal.show('borrar-sancion-' + sancionId)
    }

    function onSancionClose(modalName, actualizar) {
        instance.proxy.$modal.hide(modalName)
        if (actualizar) {
            refreshAbonado()
        } 
    }

    function onBorrarSancionClose(modalName, borrado) {
        if (modalName) {
            instance.proxy.$modal.hide(modalName)
        }
        modalBorrarSancion.value = null
        if (borrado) {
            refreshAbonado()
        }
    }

    function onAddMonedero() {
        instance.proxy.$modal.show('addMonedero')
    }

    function onMonederoClose(modalName, actualizar) {
        instance.proxy.$modal.hide(modalName)
        if (actualizar) {
            refreshAbonado()
        }
    }

    function onCambiarAsiento() {
        instance.proxy.$modal.show('cambiar-asiento-' + abonado.value.id)
    }

    function onCambiarAsientoClose(modalName, cambiado) {
        instance.proxy.$modal.hide(modalName)
        modalCambiarAsiento.value = false
        if (cambiado) {
            refreshAbonado()
        }
    }

    function onCambiarPrecio() {
        instance.proxy.$modal.show('cambiar-precio-' + abonado.value.id)
    }

    function onCambiarPrecioClose(modalName, cambiado) {
        instance.proxy.$modal.hide(modalName)
        modalCambiarPrecio.value = false
        if (cambiado) {
            success('Precio actualizado')
            refreshAbonado()
        }
    }

    async function onEditarTransaccion(transaccion) {
        selectedTransaccion.value = transaccion
        await nextTick()
        instance.proxy.$modal.show('editTransaccion-' + transaccion.id)
    }

    function onEditarTransaccionClose(modalName, actualizar) {
        instance.proxy.$modal.hide(modalName)
        selectedTransaccion.value = null
        if (actualizar) {
            refreshAbonado()
        }
    }

    function getHeaderOptions() {
        if (!abonado.value) return []

        const options = [
            {
                to: {
                    name: 'back.abonados.editar',
                    params: { lista_id: props.lista_id, abonado_id: abonado.value.id }
                },
                label: 'Editar',
                icon: 'edit'
            }
        ]

        if (abonado.value.esta_renovado) {
            options.push(
                {
                    type: 'url',
                    url: abonado.value.url_imprimir,
                    label: 'Imprimir',
                    icon: 'print'
                },
                {
                    type: 'action',
                    action: () => enviarAbono(),
                    label: 'Enviar',
                    icon: 'paper-plane'
                },
                {
                    to: {
                        name: 'back.abonados.liberar.asiento',
                        params: {
                            lista_id: props.lista_id,
                            abonado_id: abonado.value.id
                        }
                    },
                    label: 'Liberar asiento',
                    icon: 'lock-open'
                }
            )
        }

        return options
    }

    // ==================== METHODS FOR DOCUMENTO CARD ====================

    async function openEditDocumentoModal(doc) {
        state.modals.selectedDocumento = doc
        await nextTick()
        instance.proxy.$modal.show('editar-' + doc.id)
    }

    function closeEditDocumentoModal() {
        if (state.modals.selectedDocumento) {
            instance.proxy.$modal.hide('editar-' + state.modals.selectedDocumento.id)
        }
        state.modals.selectedDocumento = null
    }

    async function openBorrarDocumentoModal(doc) {
        state.modals.selectedDocumento = doc
        await nextTick()
        instance.proxy.$modal.show('borrar-doc-' + doc.id)
    }

    function closeDeleteDocumentoModal() {
        if (state.modals.selectedDocumento) {
            instance.proxy.$modal.hide('borrar-doc-' + state.modals.selectedDocumento.id)
        }
        state.modals.selectedDocumento = null
    }

    async function confirmDeleteDocumento(docId) {
        closeDeleteDocumentoModal()
        await deleteDocumento(docId)
    }

    async function confirmEditDocumento(documento) {
        closeEditDocumentoModal()
        await updateDocumento(documento)
    }

    function previewImagen(doc) {
        imagenPreview.value = doc.url
        instance.proxy.$modal.show('preview-imagen-doc')
    }

    function closePreviewModal() {
        instance.proxy.$modal.hide('preview-imagen-doc')
    }
</script>
